import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useParams} from "react-router";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import Button from "../../../../components/button/button.jsx";
import {useEffect, useMemo, useState} from "react";
import useFetchData from "../hooks/use-fetch-data.js";
import {useQuery} from "@tanstack/react-query";
import UsageRecordsService from "../../../../services/web/usage-records-service.js";
import userService from "../../../../services/web/user-service.js";
import {toast} from "react-toastify";
import ProjectUtils from "../../../../utils/project-utils.js";
import {FaPlus} from "react-icons/fa";
import Swal from "sweetalert2";
import { FaTriangleExclamation, FaCircleExclamation } from "react-icons/fa6";

function getCnhUiFromStatus(status) {
    if (status === "missing") return { status: "danger", text: "Não possui CNH", icon: <FaTriangleExclamation className="text-red-500" /> };
    if (status === "expired") return { status: "danger", text: "CNH vencida", icon: <FaTriangleExclamation className="text-red-500" /> };
    if (status === "expiring") return { status: "warning", text: "CNH a vencer", icon: <FaCircleExclamation className="text-amber-500" /> };
    return { status: null, text: "", icon: null };
}

const initialForm = {
    vehicle: null,
    conductor: null,
    date_departure: "",
    date_return: "",
    km_departure: "",
    km_return: "",
    note: "",
};

export default function UsageRecordForm() {
    const navigate = navigateHook();
    const { id } = useParams();
    const fetchData = useFetchData();

    const [form, setForm] = useState(initialForm);

    const { data: usageRecordId, isLoading, refetch } = useQuery({
        queryKey: ['usage-form-id', id],
        queryFn: () => UsageRecordsService.getById(id),
        enabled: !!id,
    });

    const isEdit = !!id;
    const record = usageRecordId?.data ?? usageRecordId;

    const { data: conductorCnhStatus } = useQuery({
        queryKey: ["usage-conductor-cnh-status", record?.conductor],
        queryFn: () => userService.conductorCnhStatus(record.conductor),
        enabled: !!record?.conductor,
    });

    const parseApiDateTime = (str) => {
        if (!str) return null;
        const d = new Date(str.replace(" ", "T"));
        return Number.isNaN(d.getTime()) ? null : d;
    };

    useEffect(() => {
        if (!record) return;

        setForm((prev) => ({
            ...prev,
            vehicle: fetchData.vehicles.find((v) => v.value === record.vehicles_id) ?? { value: record.vehicles_id, label: String(record.vehicles_id) },
            conductor: fetchData.conductors.find((c) => c.value === record.conductor) ?? { value: record.conductor, label: String(record.conductor) },

            date_departure: parseApiDateTime(record.date_departure),
            date_return: parseApiDateTime(record.date_return),
            km_departure: record.km_departure ?? "",
            km_return: record.km_return ?? "",
            note: record.note ?? "",
        }));
    }, [record, fetchData.vehicles, fetchData.conductors]);

    const vehicleId = form.vehicle?.value;

    const { data: vehicleDetails } = useQuery({
        queryKey: ['vehicle-by-id', vehicleId],
        queryFn: () => UsageRecordsService.geIdVehicle(vehicleId),
        enabled: !!vehicleId,
    });

    useEffect(() => {
        setForm((prev) => {
            if (isEdit) return prev;
            if (!vehicleId) { return { ...prev, km_departure: "", km_return: ""}; }
            if (!vehicleDetails) return prev;
            if (!vehicleDetails.km_control) return { ...prev, km_departure: "", km_return: ""};
            return { ...prev, km_departure: vehicleDetails.actual_km ?? ""};
        });
    }, [vehicleId, vehicleDetails]);

    const isGarnished = vehicleDetails?.status === "GARNISHED";

    const kmEnabled = !!(vehicleId && vehicleDetails?.km_control);
    const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const pad2 =(n) => String(n).padStart(2, "0");

    const formatBRDateTime = (d) => {
        if (!d) return "";
        const date = d instanceof Date ? d : new Date(d);
        if (Number.isNaN(date.getTime())) return "";
        return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
    }

    const formatVehicleName = (vehicle) => {
        if (!vehicle) return "";
        let format = vehicle.prefix ?? "";

        if (vehicle.plate) format += ` - ${vehicle.plate}`;
        if (vehicle.brand) format += ` - ${vehicle.brand}`;
        if (vehicle.model) format += ` - ${vehicle.model}`;

        const className = vehicle.vehicleClasses?.name;
        if (vehicle.vehicle_classes_id && className) format += ` - ${className}`;

        return format;
    };

    useEffect(() => {
        if (!vehicleId || !vehicleDetails) return;

        const label = formatVehicleName(vehicleDetails) || String(vehicleId);

        setForm((prev) => ({
            ...prev,
            vehicle: { value: vehicleId, label },
        }));
    }, [vehicleId, vehicleDetails]);

    const vehicleOptions = useMemo(() => {
        if (!form.vehicle?.value) return fetchData.vehicles;

        const id = form.vehicle.value;
        const exists = fetchData.vehicles.some((v) => v.value === id);

        if (exists) return fetchData.vehicles;

        const label = vehicleDetails ? formatVehicleName(vehicleDetails) : String(id);
        const injected = { value: id, label: label || String(id) };

        return [injected, ...fetchData.vehicles];
    }, [fetchData.vehicles, form.vehicle?.value, vehicleDetails]);

    const validate = () => {
        if (!form.vehicle?.value) return 'O campo "Veículo" é obrigatório.';
        if (!form.conductor?.value) return 'O campo "Condutor" é obrigatório.';
        if (!form.date_departure) return 'O campo "Data de saída" é obrigatório.';
        return null;
    };

    const buildFormData = () => {
        const fd = new FormData();

        fd.append("vehicles_id", String(form.vehicle.value));
        fd.append("conductor", String(form.conductor.value));
        fd.append("date_departure", formatBRDateTime(form.date_departure));
        fd.append("km_departure", kmEnabled && form.km_departure !== "" ? String(form.km_departure) : "");
        fd.append("date_return", form.date_return ? formatBRDateTime(form.date_return) : "");
        fd.append("km_return",(!kmEnabled || form.km_return === "" || form.km_return === null) ? "" : String(form.km_return));
        fd.append("note", form.note ? String(form.note) : "");

        return fd;
    };

    const handleSave = async () => {
        try {
            const err = validate();
            if (err) { ProjectUtils.swalErrorMessage("", err, "Ok"); return; }

            const fd = buildFormData();

            for (const [k, v] of fd.entries()) {
                console.log(k, v)
            }

            const result = id
                ? await UsageRecordsService.update(id, fd)
                : await UsageRecordsService.create(fd);

            ProjectUtils.success(!id);
            if (!id) {
                const newId = result?.id ?? result?.data?.id;
                const urlDev = `/usage-record/form/${newId}`;
                navigate(urlDev);
            }
            refetch?.();
        } catch (error) {
            console.error(error);

            let message = "Não foi possível salvar.";
            const status = error?.response?.status;
            const data = error?.response?.data;

            if (status && status !== 500) {
                message = data?.message ?? message;
            }

            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    };

    const handleDelete = () => {
        if (!id) return;
        Swal.fire({
            title: "Excluir",
            text: `Excluir o registro de uso?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#063A6C",
            confirmButtonText: "Sim",
            cancelButtonColor: "#c10007",
            cancelButtonText: "Não",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                return UsageRecordsService.delete(id).then(() => {
                    ProjectUtils.success(true, "Excluído com sucesso");
                    navigateToList();
                })
            }
        })
    };

    const navigateToList = () => {
        const urlDev = "/usage-record";
        navigate(urlDev);
    };

    const newUsageRecord = () => {
        const urlDev = "/usage-record/form";
        navigate(urlDev);

        setForm(initialForm);
    };

    const cnhUi = useMemo(() => getCnhUiFromStatus(conductorCnhStatus), [conductorCnhStatus]);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Registro de uso</Template.Title>
                {isEdit && (
                    <Button target="primary-outline" onClick={newUsageRecord}><FaPlus />Adicionar registro</Button>
                )}
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <SelectComponent
                                id="vehicle"
                                label="Veículo *"
                                classNameWrapper="flex-1"
                                options={vehicleOptions}
                                value={form.vehicle}
                                onChange={(val) => updateField('vehicle', val)}
                                isDisabled={isEdit}
                            />
                            <SelectComponent
                                id="conductor"
                                label="Condutor *"
                                classNameWrapper="flex-1"
                                options={fetchData.conductors}
                                value={form.conductor}
                                onChange={(val) => updateField('conductor', val)}
                                isDisabled={isEdit}
                                status={cnhUi.status}
                                helperText={cnhUi.text}
                                customIcon={cnhUi.icon}
                            />
                        </div>
                        <div className="flex gap-4 pt-3 items-center">
                            <p className="font-semibold text-sm pr-4">Saída</p>
                            <InputDateTime
                                id="date_departure"
                                label="Data/Hora *"
                                dateFormat="LL"
                                selected={form.date_departure}
                                onChange={(date) => updateField("date_departure", date)}
                            />
                            <InputForm
                                id="km_departure"
                                label="Km *"
                                integerOnly
                                type="number"
                                min={0}
                                value={form.km_departure}
                                onChange={(e) => updateField("km_departure", Number(e.target.value))}
                                disabled={!kmEnabled}
                            />
                        </div>
                        <div className="flex gap-4 pt-3 items-center">
                            <p className="font-semibold text-sm">Retorno</p>
                            <InputDateTime
                                id="date_return"
                                label="Data/Hora"
                                dateFormat="LL"
                                selected={form.date_return}
                                onChange={(date) => updateField("date_return", date)}
                            />
                            <InputForm
                                id="km_return"
                                label="Km"
                                integerOnly
                                type="number"
                                min={0}
                                value={form.km_return}
                                onChange={(e) => updateField("km_return", Number(e.target.value))}
                                disabled={!kmEnabled}
                            />
                        </div>
                        <div className="pt-3 flex">
                            <InputTextarea
                                id="note"
                                label="Observação"
                                className="flex-1"
                                style={{ height: "200px"}}
                                value={form.note}
                                onChange={(e) => updateField("note", e.target.value)}
                            />
                        </div>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className="flex justify-end gap-2">
                            {isEdit && (
                                <Button target="danger" onClick={handleDelete} disabled={isGarnished} actionFeedbackType='delete'>Excluir</Button>
                            )}
                            <Button onClick={handleSave} disabled={isGarnished} actionFeedbackType='save'>Salvar</Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}
